// pages/Settings.jsx
//
// Modelo de interacción: TODO se aplica al instante y se persiste al instante.
// La versión anterior mezclaba dos modelos (modo/densidad/menú se aplicaban
// solos; colores y tipografía exigían "Guardar"), y nada en la pantalla decía
// cuál era cuál: "Deshacer cambios" solo deshacía la mitad y la vista previa
// mostraba el tema guardado, no el borrador. Aquí la app entera es la vista
// previa, y la reversibilidad se da con "Deshacer" (vuelve al estado con el
// que entraste a la página) y un "Restablecer" por sección.
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  ButtonBase,
  TextField,
  Snackbar,
  Alert,
  Tooltip,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import PaletteIcon from "@mui/icons-material/PaletteOutlined";
import TextFieldsIcon from "@mui/icons-material/TextFieldsOutlined";
import ViewSidebarIcon from "@mui/icons-material/ViewSidebarOutlined";
import AccessibilityIcon from "@mui/icons-material/AccessibilityNewOutlined";
import ContrastIcon from "@mui/icons-material/ContrastOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAltOutlined";
import UndoIcon from "@mui/icons-material/UndoRounded";
import CheckIcon from "@mui/icons-material/CheckRounded";
import SearchIcon from "@mui/icons-material/SearchRounded";
import WarningIcon from "@mui/icons-material/WarningAmberRounded";
import DashboardLayout from "../layouts/DashboardLayout";
import { ThemeContext, TEXT_SCALES } from "../context/ThemeContext";
import { SHAPE, type, useM3 } from "../theme/m3";
import MARCA from "../config/marca";

/* ===================== Color: utilidades ===================== */

/**
 * Acepta lo que la gente realmente escribe —"1a746b", "#ABC", " #1A746B "—
 * y devuelve "#RRGGBB" en mayúsculas, o null si no hay forma de interpretarlo.
 * El campo anterior solo admitía 6 dígitos con '#' y se quedaba mudo.
 */
export const normalizeHex = (input) => {
  if (typeof input !== "string") return null;
  const v = input.trim().replace(/^#/, "");
  if (/^[0-9A-Fa-f]{3}$/.test(v)) {
    return `#${v
      .split("")
      .map((c) => c + c)
      .join("")
      .toUpperCase()}`;
  }
  if (/^[0-9A-Fa-f]{6}$/.test(v)) return `#${v.toUpperCase()}`;
  return null;
};

const toRgb = (hex) => {
  const h = normalizeHex(hex) || "#000000";
  return {
    r: parseInt(h.slice(1, 3), 16),
    g: parseInt(h.slice(3, 5), 16),
    b: parseInt(h.slice(5, 7), 16),
  };
};

// Luminancia relativa según WCAG 2.1.
const luminance = (hex) => {
  const { r, g, b } = toRgb(hex);
  const ch = [r, g, b].map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2];
};

export const contrastRatio = (a, b) => {
  const l1 = luminance(a);
  const l2 = luminance(b);
  const [hi, lo] = l1 > l2 ? [l1, l2] : [l2, l1];
  return (hi + 0.05) / (lo + 0.05);
};

/** Texto legible (negro o blanco) sobre un fondo dado. */
const bestTextOn = (hex) =>
  contrastRatio(hex, "#FFFFFF") >= contrastRatio(hex, "#000000") ? "#FFFFFF" : "#000000";

/* ===================== Presets ===================== */

// El primer preset es el valor que realmente usa ThemeContext cuando no hay
// nada guardado, así "Restablecer" deja la app como recién instalada. Antes
// restauraba "#1a746b"/"#ff470a" mientras el defecto real era el de MARCA:
// restablecer dejaba un estado que nunca había sido el inicial.
const DEFAULT_BRAND = { primary: MARCA.colorPrimario, secondary: MARCA.colorSecundario };

const BRAND_PRESETS = [
  { name: MARCA.nombre || "Por defecto", ...DEFAULT_BRAND },
  { name: "Verde TRS", primary: "#1A746B", secondary: "#FF470A" },
  { name: "Índigo", primary: "#6366F1", secondary: "#0EA5E9" },
  { name: "Azul MUI", primary: "#1976D2", secondary: "#DC004E" },
  { name: "Ámbar", primary: "#B45309", secondary: "#10B981" },
  { name: "Violeta", primary: "#7C3AED", secondary: "#F59E0B" },
  { name: "Grafito", primary: "#334155", secondary: "#0EA5E9" },
].filter(
  // Si la marca coincide con un preset, no ofrecer el mismo par dos veces.
  (p, i, arr) => arr.findIndex((o) => o.primary === p.primary && o.secondary === p.secondary) === i
);

const MONO_JETBRAINS = "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";
const MONO_FIRA = "'Fira Code', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";
const SANS_TAIL = "system-ui, -apple-system, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif";
const INTER_BODY = `Inter Variable, Inter, ${SANS_TAIL}`;

// `group` existe solo para la UI: 16 nombres en una lista plana no se pueden
// comparar. Cada opción se dibuja con la fuente que ofrece, en vez de
// describirla con palabras.
const FONT_PRESETS = [
  {
    label: "Inter",
    group: "Sans",
    body: INTER_BODY,
    heading: "Inter Variable, Inter, sans-serif",
    mono: MONO_JETBRAINS,
  },
  {
    label: "Roboto Flex",
    group: "Sans",
    body: `Roboto Flex, Roboto, ${SANS_TAIL}`,
    heading: "Roboto Flex, Roboto, sans-serif",
    mono: MONO_JETBRAINS,
  },
  {
    label: "Plus Jakarta Sans",
    group: "Sans",
    body: `Plus Jakarta Sans Variable, Plus Jakarta Sans, ${SANS_TAIL}`,
    heading: "Plus Jakarta Sans Variable, Plus Jakarta Sans, sans-serif",
    mono: MONO_FIRA,
  },
  {
    label: "Urbanist",
    group: "Sans",
    body: `Urbanist Variable, Urbanist, ${SANS_TAIL}`,
    heading: "Urbanist Variable, Urbanist, sans-serif",
    mono: MONO_FIRA,
  },
  {
    label: "Outfit",
    group: "Sans",
    body: `Outfit Variable, Outfit, ${SANS_TAIL}`,
    heading: "Outfit Variable, Outfit, sans-serif",
    mono: MONO_FIRA,
  },
  {
    label: "DM Sans",
    group: "Sans",
    body: `DM Sans Variable, DM Sans, ${SANS_TAIL}`,
    heading: "DM Sans Variable, DM Sans, sans-serif",
    mono: MONO_FIRA,
  },
  {
    label: "Work Sans",
    group: "Sans",
    body: `Work Sans Variable, Work Sans, ${SANS_TAIL}`,
    heading: "Work Sans Variable, Work Sans, sans-serif",
    mono: MONO_FIRA,
  },
  {
    label: "Source Sans 3",
    group: "Sans",
    body: `Source Sans 3 Variable, Source Sans 3, ${SANS_TAIL}`,
    heading: "Source Sans 3 Variable, Source Sans 3, sans-serif",
    mono: MONO_FIRA,
  },
  {
    label: "Open Sans",
    group: "Sans",
    body: `Open Sans Variable, Open Sans, ${SANS_TAIL}`,
    heading: "Open Sans Variable, Open Sans, sans-serif",
    mono: MONO_FIRA,
  },
  {
    label: "IBM Plex Sans",
    group: "Sans",
    body: `IBM Plex Sans Variable, IBM Plex Sans, ${SANS_TAIL}`,
    heading: "IBM Plex Sans Variable, IBM Plex Sans, sans-serif",
    mono: MONO_FIRA,
  },
  {
    label: "Montserrat",
    group: "Sans",
    body: `Montserrat Variable, Montserrat, ${SANS_TAIL}`,
    heading: "Montserrat Variable, Montserrat, sans-serif",
    mono: MONO_FIRA,
  },
  {
    label: "Nunito",
    group: "Sans",
    body: `Nunito Variable, Nunito, ${SANS_TAIL}`,
    heading: "Nunito Variable, Nunito, sans-serif",
    mono: MONO_FIRA,
  },
  {
    label: "Noto Serif",
    group: "Títulos con serif",
    body: INTER_BODY,
    heading: "Noto Serif Variable, Noto Serif, Georgia, serif",
    mono: MONO_FIRA,
  },
  {
    label: "Playfair Display",
    group: "Títulos con serif",
    body: INTER_BODY,
    heading: "Playfair Display Variable, Playfair Display, Georgia, serif",
    mono: MONO_FIRA,
  },
  {
    label: "Lora",
    group: "Títulos con serif",
    body: INTER_BODY,
    heading: "Lora Variable, Lora, Georgia, serif",
    mono: MONO_FIRA,
  },
  {
    label: "Del sistema",
    group: "Sistema",
    body: SANS_TAIL,
    heading: SANS_TAIL,
    mono: "ui-monospace, SFMono-Regular, Menlo, Consolas, 'Liberation Mono', monospace",
  },
];

const FONT_GROUPS = ["Sans", "Títulos con serif", "Sistema"];

const DEFAULT_FONTS = FONT_PRESETS[0];
const DEFAULTS = {
  mode: "light",
  brand: DEFAULT_BRAND,
  fonts: { body: DEFAULT_FONTS.body, heading: DEFAULT_FONTS.heading, mono: DEFAULT_FONTS.mono },
  compact: false,
  navStyle: "sidebar",
  textScale: 1,
  motion: "system",
};

const sameFonts = (a, b) => a?.body === b?.body && a?.heading === b?.heading && a?.mono === b?.mono;

const SCALE_LABELS = { 0.9: "Pequeño", 1: "Normal", 1.1: "Grande", 1.2: "Muy grande" };

/* ===================== Primitivas de UI ===================== */

/** Tarjeta de sección: etiqueta M3 en primario, título, descripción y su propio reset. */
const Section = ({ id, icon, label, title, description, onReset, resetDisabled, children }) => {
  const m3 = useM3();
  return (
    <Box
      id={id}
      component="section"
      aria-labelledby={`${id}-title`}
      sx={{
        background: m3.surfaceContainer,
        borderRadius: `${SHAPE.xl}px`,
        p: { xs: 2, sm: 3 },
        scrollMarginTop: 88,
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ mb: 2 }}>
        <Box
          aria-hidden
          sx={{
            width: 40,
            height: 40,
            flexShrink: 0,
            borderRadius: SHAPE.full,
            display: "grid",
            placeItems: "center",
            background: m3.primaryContainer,
            color: m3.onPrimaryContainer,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ ...type.labelMedium, color: m3.primary, fontWeight: 700 }}>
            {label}
          </Typography>
          <Typography id={`${id}-title`} sx={{ ...type.titleLarge, color: m3.onSurface }}>
            {title}
          </Typography>
          <Typography sx={{ ...type.bodyMedium, color: m3.onSurfaceVariant, mt: 0.5 }}>
            {description}
          </Typography>
        </Box>
        {onReset && (
          // Salida de emergencia por sección: antes solo existía un
          // "Restaurar por defecto" global de todo-o-nada.
          <Tooltip title={`Restablecer ${title.toLowerCase()}`}>
            <span>
              <Button
                size="small"
                onClick={onReset}
                disabled={resetDisabled}
                startIcon={<RestartAltIcon />}
                sx={{ ...type.labelLarge, borderRadius: SHAPE.full, flexShrink: 0 }}
              >
                Restablecer
              </Button>
            </span>
          </Tooltip>
        )}
      </Stack>
      {children}
    </Box>
  );
};

/**
 * Opción visual seleccionable. Semántica de radio real (role + aria-checked +
 * foco visible), porque un `Box` con onClick no lo anuncia ningún lector de
 * pantalla ni se alcanza con el teclado.
 */
const OptionCard = ({ selected, onSelect, title, caption, visual, minWidth = 136 }) => {
  const m3 = useM3();
  return (
    <ButtonBase
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      focusRipple
      sx={{
        flex: `1 1 ${minWidth}px`,
        minWidth,
        display: "block",
        textAlign: "left",
        p: 1.25,
        borderRadius: `${SHAPE.lg}px`,
        background: selected ? m3.secondaryContainer : m3.surfaceLowest,
        color: selected ? m3.onSecondaryContainer : m3.onSurface,
        border: `2px solid ${selected ? m3.primary : m3.outline}`,
        transition: "background-color .18s ease, border-color .18s ease",
        "&:hover": { background: selected ? m3.secondaryContainer : m3.stateLayer(0.06) },
        "&:focus-visible": { outline: `3px solid ${alpha(m3.primary, 0.5)}`, outlineOffset: 2 },
      }}
    >
      {visual}
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: visual ? 1 : 0 }}>
        <Typography sx={{ ...type.labelLarge, flex: 1 }} noWrap>
          {title}
        </Typography>
        {selected && <CheckIcon sx={{ fontSize: 18, color: m3.primary }} />}
      </Stack>
      {caption && (
        <Typography sx={{ ...type.bodySmall, color: m3.onSurfaceVariant, display: "block" }}>
          {caption}
        </Typography>
      )}
    </ButtonBase>
  );
};

const RadioRow = ({ label, children }) => (
  <Box role="radiogroup" aria-label={label}>
    <Stack direction="row" spacing={1.25} useFlexGap flexWrap="wrap">
      {children}
    </Stack>
  </Box>
);

/* --- Diagramas: muestran la forma del layout en vez de describirla ------ */

const NavDiagram = ({ variant, color }) => {
  const bar = { fill: color, opacity: 0.9 };
  const dim = { fill: color, opacity: 0.25 };
  return (
    <Box
      component="svg"
      viewBox="0 0 96 56"
      aria-hidden
      sx={{ width: "100%", height: 56, display: "block" }}
    >
      {variant === "sidebar" && (
        <>
          <rect x="4" y="4" width="26" height="48" rx="3" {...bar} />
          <rect x="34" y="4" width="58" height="8" rx="2" {...dim} />
          <rect x="34" y="16" width="58" height="36" rx="3" {...dim} />
        </>
      )}
      {variant === "rail" && (
        <>
          <rect x="4" y="4" width="10" height="48" rx="3" {...bar} />
          <rect x="18" y="4" width="74" height="8" rx="2" {...dim} />
          <rect x="18" y="16" width="74" height="36" rx="3" {...dim} />
        </>
      )}
      {variant === "top" && (
        <>
          <rect x="4" y="4" width="88" height="12" rx="3" {...bar} />
          <rect x="4" y="20" width="88" height="32" rx="3" {...dim} />
        </>
      )}
    </Box>
  );
};

const ModeDiagram = ({ variant, primary }) => {
  // "auto" se dibuja partido para que se entienda que depende del sistema.
  const panels =
    variant === "auto"
      ? [
          { bg: "#FFFFFF", fg: "#1F2937" },
          { bg: "#0B1324", fg: "#E5E7EB" },
        ]
      : variant === "dark"
        ? [{ bg: "#0B1324", fg: "#E5E7EB" }]
        : [{ bg: "#FFFFFF", fg: "#1F2937" }];
  return (
    <Stack direction="row" spacing={0} sx={{ height: 44, borderRadius: 1.5, overflow: "hidden" }}>
      {panels.map((p, i) => (
        <Box key={i} sx={{ flex: 1, bgcolor: p.bg, p: 0.75 }}>
          <Box sx={{ height: 5, borderRadius: 1, bgcolor: primary, mb: 0.5 }} />
          <Box sx={{ height: 4, borderRadius: 1, bgcolor: p.fg, opacity: 0.55, mb: 0.5 }} />
          <Box sx={{ height: 4, borderRadius: 1, bgcolor: p.fg, opacity: 0.3, width: "70%" }} />
        </Box>
      ))}
    </Stack>
  );
};

const DensityDiagram = ({ compact, color }) => (
  <Stack spacing={compact ? 0.375 : 0.875} sx={{ height: 44, justifyContent: "center", px: 0.5 }}>
    {[0, 1, 2, 3].map((i) => (
      <Box
        key={i}
        sx={{ height: compact ? 5 : 7, borderRadius: 1, bgcolor: color, opacity: 0.3 + i * 0.1 }}
      />
    ))}
  </Stack>
);

/* --- Campo de color con validación visible y commit diferido ----------- */

/**
 * El input nativo de color dispara `change` en cada paso del arrastre. Pintar
 * el theme completo en cada evento traba la página, así que el swatch se mueve
 * con estado local y el commit al contexto va con un pequeño retardo.
 */
const ColorField = ({ label, value, onCommit }) => {
  const m3 = useM3();
  const [text, setText] = useState(value);
  const [focused, setFocused] = useState(false);
  const timer = useRef(null);

  // Mientras el campo tiene el foco no se sobreescribe lo que la persona está
  // escribiendo: si no, al teclear "ffff00" el valor se normaliza en cuanto
  // "fff" ya es válido y las teclas siguientes se agregan a "#FFFFFF".
  useEffect(() => {
    if (!focused) setText(value);
  }, [value, focused]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const commitLater = (hex) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => onCommit(hex), 120);
  };

  const handleText = (raw) => {
    setText(raw);
    // Solo se aplica la forma completa de 6 dígitos mientras se escribe; la
    // abreviada (#RGB) se expande al salir del campo, cuando ya se sabe que
    // eso era todo lo que se quería escribir.
    const hex = normalizeHex(raw);
    if (hex && raw.trim().replace(/^#/, "").length === 6) commitLater(hex);
  };

  const handleBlur = () => {
    setFocused(false);
    const hex = normalizeHex(text);
    if (hex) {
      setText(hex);
      clearTimeout(timer.current);
      onCommit(hex);
    } else {
      setText(value); // entrada sin sentido: volver al color vigente
    }
  };

  const parsed = normalizeHex(text);
  // Validar al salir del campo, no en cada tecla: marcar "1a7" en rojo
  // mientras se escribe el color es ruido, no ayuda.
  const invalid = !focused && parsed === null;

  return (
    <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ flex: "1 1 240px" }}>
      <Box
        component="label"
        sx={{
          width: 48,
          height: 48,
          flexShrink: 0,
          borderRadius: `${SHAPE.md}px`,
          border: `1px solid ${m3.outline}`,
          overflow: "hidden",
          cursor: "pointer",
          bgcolor: parsed || value,
          "&:focus-within": { outline: `3px solid ${alpha(m3.primary, 0.5)}`, outlineOffset: 2 },
        }}
      >
        <Box
          component="input"
          type="color"
          value={parsed || value}
          onChange={(e) => {
            const hex = e.target.value.toUpperCase();
            setText(hex);
            commitLater(hex);
          }}
          aria-label={label}
          sx={{ opacity: 0, width: "100%", height: "100%", border: 0, cursor: "pointer" }}
        />
      </Box>
      <TextField
        label={label}
        size="small"
        value={text}
        onChange={(e) => handleText(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={handleBlur}
        error={invalid}
        helperText={invalid ? "Formato #RRGGBB o #RGB." : " "}
        slotProps={{
          input: { sx: { borderRadius: `${SHAPE.md}px` } },
          htmlInput: { spellCheck: false, autoComplete: "off" },
        }}
        sx={{ flex: 1, minWidth: 0 }}
      />
    </Stack>
  );
};

/* ===================== Página ===================== */

const Settings = () => {
  const theme = useTheme();
  const m3 = useM3();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const {
    mode,
    setMode,
    brand,
    setBrand,
    fonts,
    setFonts,
    compact,
    setCompact,
    navStyle,
    setNavStyle,
    textScale,
    setTextScale,
    motion,
    setMotion,
    reducedMotion,
  } = useContext(ThemeContext);

  // Estado con el que se entró a la página: es el destino de "Deshacer".
  // Así hay vuelta atrás aunque todo se aplique en vivo.
  const entry = useRef({ mode, brand, fonts, compact, navStyle, textScale, motion });
  const [query, setQuery] = useState("");
  const [toast, setToast] = useState(null);
  const [confirmReset, setConfirmReset] = useState(false);

  const current = { mode, brand, fonts, compact, navStyle, textScale, motion };

  const differs = (a, b) =>
    a.mode !== b.mode ||
    a.brand.primary !== b.brand.primary ||
    a.brand.secondary !== b.brand.secondary ||
    !sameFonts(a.fonts, b.fonts) ||
    a.compact !== b.compact ||
    a.navStyle !== b.navStyle ||
    a.textScale !== b.textScale ||
    a.motion !== b.motion;

  const dirty = differs(current, entry.current);
  const isDefault = !differs(current, DEFAULTS);

  const apply = (snap) => {
    setMode(snap.mode);
    setBrand(snap.brand);
    setFonts(snap.fonts);
    setCompact(snap.compact);
    setNavStyle(snap.navStyle);
    setTextScale(snap.textScale);
    setMotion(snap.motion);
  };

  const fontIdx = useMemo(() => FONT_PRESETS.findIndex((p) => sameFonts(p, fonts)), [fonts]);

  const activePreset = BRAND_PRESETS.find(
    (p) => p.primary === brand.primary && p.secondary === brand.secondary
  );

  /* --- Contraste: avisar antes de que la app quede ilegible ----------- */
  const contrast = useMemo(() => {
    const onPrimary = bestTextOn(brand.primary);
    const ratioText = contrastRatio(brand.primary, onPrimary);
    const ratioSurface = contrastRatio(brand.primary, theme.palette.background.default);
    return {
      ratioText,
      ratioSurface,
      // 4.5:1 es el mínimo AA para texto; 3:1 para distinguir el color del fondo.
      textFails: ratioText < 4.5,
      surfaceFails: ratioSurface < 3,
    };
  }, [brand.primary, theme.palette.background.default]);

  /* --- Buscador: con ~20 ajustes, leer todo para encontrar uno es caro - */
  const SECTIONS = useMemo(
    () => [
      {
        id: "apariencia",
        label: "Apariencia",
        icon: <ContrastIcon fontSize="small" />,
        keywords: "apariencia modo tema claro oscuro automatico densidad compacta comoda",
      },
      {
        id: "color",
        label: "Color",
        icon: <PaletteIcon fontSize="small" />,
        keywords: "color colores marca primario secundario paleta hex contraste",
      },
      {
        id: "tipografia",
        label: "Tipografía",
        icon: <TextFieldsIcon fontSize="small" />,
        keywords: "tipografia fuente letra font inter roboto serif tamano",
      },
      {
        id: "navegacion",
        label: "Navegación",
        icon: <ViewSidebarIcon fontSize="small" />,
        keywords: "navegacion menu sidebar lateral rail angosto barra superior top layout",
      },
      {
        id: "accesibilidad",
        label: "Accesibilidad",
        icon: <AccessibilityIcon fontSize="small" />,
        keywords: "accesibilidad tamano texto escala movimiento animacion reducir contraste",
      },
    ],
    []
  );

  const q = query.trim().toLowerCase();
  const visible = (id) => {
    if (!q) return true;
    const s = SECTIONS.find((x) => x.id === id);
    return s ? s.keywords.includes(q) || s.label.toLowerCase().includes(q) : true;
  };
  const anyVisible = SECTIONS.some((s) => visible(s.id));

  const goTo = (id) =>
    document.getElementById(id)?.scrollIntoView({
      // Respetar la propia preferencia de movimiento del usuario.
      behavior: reducedMotion ? "auto" : "smooth",
      block: "start",
    });

  return (
    <DashboardLayout>
      <Box sx={{ minHeight: "100%", background: m3.surfaceLow, pb: dirty ? 14 : 8 }}>
        <Box sx={{ maxWidth: 1040, mx: "auto", px: 2 }}>
          {/* --- Large top app bar (M3) --- */}
          <Box sx={{ pt: { xs: 3, md: 5 }, pb: 2 }}>
            <Typography sx={{ ...type.displaySmall, color: m3.onSurface }}>
              Configuración
            </Typography>
            <Typography sx={{ ...type.bodyMedium, color: m3.onSurfaceVariant, mt: 0.5 }}>
              Los cambios se aplican y se guardan al instante en este dispositivo.
            </Typography>
          </Box>

          <TextField
            fullWidth
            size="small"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar un ajuste…"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                sx: { borderRadius: SHAPE.full, background: m3.surfaceLowest },
              },
            }}
            sx={{ mb: 3 }}
          />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "180px 1fr" },
              alignItems: "start",
              gap: 3,
            }}
          >
            {/* ================= Índice lateral ================= */}
            {!isMobile && (
              <Stack
                component="nav"
                aria-label="Secciones de configuración"
                spacing={0.25}
                sx={{ position: "sticky", top: 16 }}
              >
                {SECTIONS.map((n) => (
                  <ButtonBase
                    key={n.id}
                    onClick={() => goTo(n.id)}
                    disabled={!visible(n.id)}
                    sx={{
                      justifyContent: "flex-start",
                      gap: 1.25,
                      px: 1.75,
                      py: 1,
                      borderRadius: SHAPE.full,
                      color: visible(n.id) ? m3.onSurfaceVariant : alpha(m3.onSurfaceVariant, 0.4),
                      ...type.labelLarge,
                      "&:hover": { background: m3.stateLayer(0.08) },
                      "&:focus-visible": { outline: `3px solid ${alpha(m3.primary, 0.5)}` },
                    }}
                  >
                    {n.icon}
                    {n.label}
                  </ButtonBase>
                ))}
              </Stack>
            )}

            {/* ================= Contenido ================= */}
            <Stack spacing={2} sx={{ minWidth: 0 }}>
              {!anyVisible && (
                <Box sx={{ textAlign: "center", py: 6 }}>
                  <Typography sx={{ ...type.titleMedium, color: m3.onSurface }}>
                    Sin resultados para “{query}”
                  </Typography>
                  <Button onClick={() => setQuery("")} sx={{ mt: 1, borderRadius: SHAPE.full }}>
                    Limpiar búsqueda
                  </Button>
                </Box>
              )}

              {/* ---------- 1. Apariencia ---------- */}
              {visible("apariencia") && (
                <Section
                  id="apariencia"
                  icon={<ContrastIcon />}
                  label="APARIENCIA"
                  title="Tema y densidad"
                  description="Un solo control decide el tema. Antes convivían un selector y un interruptor que se contradecían: el interruptor marcaba «claro» aunque el tema estuviera en automático."
                  onReset={() => {
                    setMode(DEFAULTS.mode);
                    setCompact(DEFAULTS.compact);
                  }}
                  resetDisabled={mode === DEFAULTS.mode && compact === DEFAULTS.compact}
                >
                  <Typography sx={{ ...type.labelLarge, color: m3.onSurfaceVariant, mb: 1 }}>
                    Tema
                  </Typography>
                  <RadioRow label="Tema de color">
                    {[
                      { v: "light", t: "Claro", c: "Siempre con fondo claro", d: "light" },
                      { v: "dark", t: "Oscuro", c: "Siempre con fondo oscuro", d: "dark" },
                      { v: "system", t: "Automático", c: "Sigue a Windows", d: "auto" },
                    ].map((o) => (
                      <OptionCard
                        key={o.v}
                        selected={mode === o.v}
                        onSelect={() => setMode(o.v)}
                        title={o.t}
                        caption={o.c}
                        visual={<ModeDiagram variant={o.d} primary={brand.primary} />}
                      />
                    ))}
                  </RadioRow>

                  <Typography sx={{ ...type.labelLarge, color: m3.onSurfaceVariant, mt: 3, mb: 1 }}>
                    Densidad
                  </Typography>
                  <RadioRow label="Densidad de la interfaz">
                    <OptionCard
                      selected={!compact}
                      onSelect={() => setCompact(false)}
                      title="Cómoda"
                      caption="Más aire entre elementos"
                      visual={<DensityDiagram compact={false} color={m3.onSurface} />}
                    />
                    <OptionCard
                      selected={compact}
                      onSelect={() => setCompact(true)}
                      title="Compacta"
                      caption="Más filas visibles por pantalla"
                      visual={<DensityDiagram compact color={m3.onSurface} />}
                    />
                  </RadioRow>
                </Section>
              )}

              {/* ---------- 2. Color ---------- */}
              {visible("color") && (
                <Section
                  id="color"
                  icon={<PaletteIcon />}
                  label="COLOR"
                  title="Colores de marca"
                  description="Elige una paleta o define los colores a mano. Te avisamos si la combinación deja texto difícil de leer."
                  onReset={() => setBrand(DEFAULTS.brand)}
                  resetDisabled={
                    brand.primary === DEFAULTS.brand.primary &&
                    brand.secondary === DEFAULTS.brand.secondary
                  }
                >
                  <Typography sx={{ ...type.labelLarge, color: m3.onSurfaceVariant, mb: 1 }}>
                    Paletas
                  </Typography>
                  <RadioRow label="Paletas predefinidas">
                    {BRAND_PRESETS.map((p) => (
                      <OptionCard
                        key={p.name}
                        selected={activePreset?.name === p.name}
                        onSelect={() => setBrand({ primary: p.primary, secondary: p.secondary })}
                        title={p.name}
                        minWidth={120}
                        visual={
                          // Dos bloques sólidos, no un degradado: el degradado
                          // anterior sugería colores intermedios que no existen,
                          // y su texto blanco fijo fallaba sobre paletas claras.
                          <Stack
                            direction="row"
                            sx={{ height: 36, borderRadius: 1.5, overflow: "hidden" }}
                          >
                            <Box sx={{ flex: 2, bgcolor: p.primary }} />
                            <Box sx={{ flex: 1, bgcolor: p.secondary }} />
                          </Stack>
                        }
                      />
                    ))}
                  </RadioRow>

                  <Typography sx={{ ...type.labelLarge, color: m3.onSurfaceVariant, mt: 3, mb: 1 }}>
                    Colores exactos
                  </Typography>
                  <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                    <ColorField
                      label="Primario"
                      value={brand.primary}
                      onCommit={(hex) => setBrand((b) => ({ ...b, primary: hex }))}
                    />
                    <ColorField
                      label="Secundario"
                      value={brand.secondary}
                      onCommit={(hex) => setBrand((b) => ({ ...b, secondary: hex }))}
                    />
                  </Stack>

                  {/* Comprobación de contraste con cifra concreta, no un simple "ojo". */}
                  <Box aria-live="polite">
                    {contrast.textFails || contrast.surfaceFails ? (
                      <Alert
                        severity="warning"
                        icon={<WarningIcon />}
                        sx={{ mt: 1, borderRadius: `${SHAPE.lg}px`, ...type.bodyMedium }}
                      >
                        {contrast.textFails && (
                          <>
                            El texto sobre el color primario queda en{" "}
                            <b>{contrast.ratioText.toFixed(1)}:1</b> y el mínimo recomendado es
                            4.5:1, así que los botones rellenos van a costar de leer.{" "}
                          </>
                        )}
                        {contrast.surfaceFails && (
                          <>
                            El primario casi no se distingue del fondo (
                            <b>{contrast.ratioSurface.toFixed(1)}:1</b>, mínimo 3:1).
                          </>
                        )}
                      </Alert>
                    ) : (
                      <Typography sx={{ ...type.bodySmall, color: m3.onSurfaceVariant, mt: 0.5 }}>
                        Contraste correcto: {contrast.ratioText.toFixed(1)}:1 para el texto sobre el
                        primario y {contrast.ratioSurface.toFixed(1)}:1 contra el fondo.
                      </Typography>
                    )}
                  </Box>
                </Section>
              )}

              {/* ---------- 3. Tipografía ---------- */}
              {visible("tipografia") && (
                <Section
                  id="tipografia"
                  icon={<TextFieldsIcon />}
                  label="TIPOGRAFÍA"
                  title="Fuente de la interfaz"
                  description="Cada opción está dibujada con su propia fuente. Un desplegable con nombres no dice nada: nadie sabe de memoria cómo se ve «Urbanist»."
                  onReset={() => setFonts(DEFAULTS.fonts)}
                  resetDisabled={sameFonts(fonts, DEFAULTS.fonts)}
                >
                  {FONT_GROUPS.map((group) => (
                    <Box key={group} sx={{ mb: 2 }}>
                      <Typography sx={{ ...type.labelLarge, color: m3.onSurfaceVariant, mb: 1 }}>
                        {group}
                      </Typography>
                      <RadioRow label={`Tipografías: ${group}`}>
                        {FONT_PRESETS.map((p, i) =>
                          p.group !== group ? null : (
                            <OptionCard
                              key={p.label}
                              selected={fontIdx === i}
                              onSelect={() =>
                                setFonts({ body: p.body, heading: p.heading, mono: p.mono })
                              }
                              title={p.label}
                              minWidth={150}
                              visual={
                                <Box sx={{ py: 0.5 }}>
                                  <Typography
                                    sx={{
                                      fontFamily: p.heading,
                                      fontSize: 20,
                                      fontWeight: 700,
                                      lineHeight: 1.2,
                                    }}
                                  >
                                    Aa Bb Cc
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontFamily: p.body,
                                      fontSize: 12,
                                      color: m3.onSurfaceVariant,
                                      lineHeight: 1.35,
                                    }}
                                    noWrap
                                  >
                                    Reporte de órdenes 1234
                                  </Typography>
                                </Box>
                              }
                            />
                          )
                        )}
                      </RadioRow>
                    </Box>
                  ))}
                </Section>
              )}

              {/* ---------- 4. Navegación ---------- */}
              {visible("navegacion") && (
                <Section
                  id="navegacion"
                  icon={<ViewSidebarIcon />}
                  label="NAVEGACIÓN"
                  title="Tipo de menú"
                  description="«Rail» y «Top App Bar» son términos de desarrollo. El diagrama muestra dónde queda el menú antes de que cambie la pantalla bajo tus pies."
                  onReset={() => setNavStyle(DEFAULTS.navStyle)}
                  resetDisabled={navStyle === DEFAULTS.navStyle}
                >
                  <RadioRow label="Tipo de menú">
                    {[
                      { v: "sidebar", t: "Lateral", c: "Menú ancho con nombres" },
                      { v: "rail", t: "Angosto", c: "Solo iconos, más espacio útil" },
                      { v: "top", t: "Superior", c: "Barra arriba, para pantallas anchas" },
                    ].map((o) => (
                      <OptionCard
                        key={o.v}
                        selected={navStyle === o.v}
                        onSelect={() => setNavStyle(o.v)}
                        title={o.t}
                        caption={o.c}
                        minWidth={150}
                        visual={<NavDiagram variant={o.v} color={brand.primary} />}
                      />
                    ))}
                  </RadioRow>
                </Section>
              )}

              {/* ---------- 5. Accesibilidad ---------- */}
              {visible("accesibilidad") && (
                <Section
                  id="accesibilidad"
                  icon={<AccessibilityIcon />}
                  label="ACCESIBILIDAD"
                  title="Texto y movimiento"
                  description="Ajustes que antes no existían y que el zoom del navegador no resuelve igual de bien en una app de escritorio."
                  onReset={() => {
                    setTextScale(DEFAULTS.textScale);
                    setMotion(DEFAULTS.motion);
                  }}
                  resetDisabled={textScale === DEFAULTS.textScale && motion === DEFAULTS.motion}
                >
                  <Typography sx={{ ...type.labelLarge, color: m3.onSurfaceVariant, mb: 1 }}>
                    Tamaño del texto
                  </Typography>
                  <RadioRow label="Tamaño del texto">
                    {TEXT_SCALES.map((s) => (
                      <OptionCard
                        key={s}
                        selected={textScale === s}
                        onSelect={() => setTextScale(s)}
                        title={SCALE_LABELS[s] || `${s}×`}
                        minWidth={104}
                        visual={
                          <Box sx={{ height: 40, display: "grid", placeItems: "center" }}>
                            <Typography
                              aria-hidden
                              sx={{ fontFamily: fonts.heading, fontSize: 14 * s * 1.6, lineHeight: 1 }}
                            >
                              Aa
                            </Typography>
                          </Box>
                        }
                      />
                    ))}
                  </RadioRow>
                  <Box
                    sx={{
                      mt: 1.5,
                      p: 2,
                      borderRadius: `${SHAPE.lg}px`,
                      background: m3.surfaceLowest,
                      border: `1px solid ${m3.outline}`,
                    }}
                  >
                    <Typography sx={{ ...type.bodySmall, color: m3.onSurfaceVariant, mb: 0.5 }}>
                      Así se lee un texto corrido con el tamaño elegido:
                    </Typography>
                    <Typography sx={{ fontSize: 14.5 * textScale, lineHeight: 1.5 }}>
                      La orden de trabajo 4821 quedó asignada al técnico de turno y se notificó al
                      cliente por correo.
                    </Typography>
                  </Box>

                  <Typography sx={{ ...type.labelLarge, color: m3.onSurfaceVariant, mt: 3, mb: 1 }}>
                    Movimiento
                  </Typography>
                  <RadioRow label="Animaciones">
                    {[
                      { v: "system", t: "Automático", c: "Según la configuración de Windows" },
                      { v: "full", t: "Completo", c: "Todas las animaciones" },
                      { v: "reduce", t: "Reducido", c: "Sin transiciones ni intro animada" },
                    ].map((o) => (
                      <OptionCard
                        key={o.v}
                        selected={motion === o.v}
                        onSelect={() => setMotion(o.v)}
                        title={o.t}
                        caption={o.c}
                        minWidth={150}
                      />
                    ))}
                  </RadioRow>
                  <Typography sx={{ ...type.bodySmall, color: m3.onSurfaceVariant, mt: 1 }}>
                    Ahora mismo: movimiento <b>{reducedMotion ? "reducido" : "completo"}</b>.
                    {reducedMotion && " La pantalla de bienvenida animada no se mostrará."}
                  </Typography>
                </Section>
              )}

              {/* ---------- Pie ---------- */}
              {!q && (
                <Stack direction="row" justifyContent="center" sx={{ pt: 1, pb: 2 }}>
                  <Button
                    startIcon={<RestartAltIcon />}
                    onClick={() => setConfirmReset(true)}
                    disabled={isDefault}
                    sx={{ ...type.labelLarge, borderRadius: SHAPE.full }}
                  >
                    Restablecer todo
                  </Button>
                </Stack>
              )}
            </Stack>
          </Box>
        </Box>

        {/* ---------- Barra de deshacer ----------
            Aparece solo si algo cambió desde que se abrió la página. Es la red
            de seguridad que reemplaza al par "Guardar / Deshacer": como todo se
            aplica en vivo, lo único que falta es poder volver atrás. */}
        {dirty && (
          <Box
            role="status"
            sx={{
              position: "fixed",
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              justifyContent: "center",
              px: 2,
              pb: 2,
              pointerEvents: "none",
              zIndex: (t) => t.zIndex.snackbar,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{
                pointerEvents: "auto",
                maxWidth: 560,
                width: "100%",
                px: 2,
                py: 1.25,
                borderRadius: `${SHAPE.lg}px`,
                background: m3.inverseSurface,
                color: m3.inverseOnSurface,
                boxShadow: 6,
              }}
            >
              <Typography sx={{ ...type.bodyMedium, flex: 1 }}>
                Cambios aplicados y guardados.
              </Typography>
              <Button
                size="small"
                startIcon={<UndoIcon />}
                onClick={() => {
                  apply(entry.current);
                  setToast("Se restauró la apariencia anterior");
                }}
                sx={{
                  ...type.labelLarge,
                  borderRadius: SHAPE.full,
                  flexShrink: 0,
                  color: m3.inverseOnSurface,
                  bgcolor: alpha(m3.inverseOnSurface, 0.14),
                  "&:hover": { bgcolor: alpha(m3.inverseOnSurface, 0.24) },
                }}
              >
                Deshacer
              </Button>
            </Stack>
          </Box>
        )}

        <Dialog
          open={confirmReset}
          onClose={() => setConfirmReset(false)}
          slotProps={{ paper: { sx: { borderRadius: `${SHAPE.xl}px`, p: 1 } } }}
        >
          <DialogTitle sx={{ ...type.headlineSmall }}>¿Restablecer todo?</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ ...type.bodyMedium }}>
              Tema, colores, tipografía, tipo de menú y accesibilidad vuelven a los valores
              originales de la aplicación. Puedes revertirlo con «Deshacer» sin salir de esta
              página.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmReset(false)} sx={{ borderRadius: SHAPE.full }}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                apply(DEFAULTS);
                setConfirmReset(false);
                setToast("Se restablecieron todos los ajustes");
              }}
              sx={{ borderRadius: SHAPE.full }}
            >
              Restablecer
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={Boolean(toast)}
          autoHideDuration={2600}
          onClose={() => setToast(null)}
          message={toast || ""}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          sx={{ mb: dirty ? 9 : 0 }}
        />
      </Box>
    </DashboardLayout>
  );
};

export default Settings;
